/**
 * Hybrid Upload Service
 * Automatically chooses between direct Lambda upload (≤6MB) and presigned URL upload (>6MB)
 * Provides transparent file upload experience regardless of file size
 */

import api from '../api/apiService';

// Configuration
const MAX_DIRECT_UPLOAD_SIZE = 6 * 1024 * 1024; // 6MB in bytes
const CHUNK_SIZE = 1024 * 1024; // 1MB chunks for progress reporting
const LARGE_FILE_THRESHOLD = 25 * 1024 * 1024; // 25MB threshold for extended timeout
const BASE_TIMEOUT = 5 * 60 * 1000; // 5 minutes base timeout
const LARGE_FILE_TIMEOUT = 30 * 60 * 1000; // 30 minutes for large files
const MAX_RETRY_ATTEMPTS = 3; // Maximum retry attempts for failed uploads
const RETRY_DELAY_BASE = 2000; // Base delay between retries (2 seconds)

export class HybridUploadService {
  constructor() {
    this.activeUploads = new Map(); // Track active uploads
  }

  /**
   * Upload a file using the appropriate method based on file size
   * @param {File} file - The file to upload
   * @param {string} influencerId - ID of the influencer
   * @param {string} folder - Target folder (raw, filtered, manual, cropped)
   * @param {function} onProgress - Progress callback function
   * @param {function} onSuccess - Success callback function
   * @param {function} onError - Error callback function
   * @returns {Promise} Upload promise
   */
  async uploadFile(file, influencerId, folder, onProgress, onSuccess, onError) {
    const uploadId = this.generateUploadId();
    
    try {
      // Validate inputs
      this.validateUploadInputs(file, influencerId, folder);
      
      // Automatic method selection based on file size
      const uploadMethod = file.size <= MAX_DIRECT_UPLOAD_SIZE ? 'direct' : 'presigned';
      
      console.log(`🔄 Starting ${uploadMethod} upload for file: ${file.name} (${this.formatFileSize(file.size)})`);
      
      // Track upload
      this.activeUploads.set(uploadId, {
        file,
        influencerId,
        folder,
        method: uploadMethod,
        startTime: Date.now(),
        status: 'started'
      });
      
      let result;
      if (uploadMethod === 'direct') {
        result = await this.directUpload(uploadId, file, influencerId, folder, onProgress);
      } else {
        result = await this.presignedUpload(uploadId, file, influencerId, folder, onProgress);
      }
      
      // Update tracking
      this.activeUploads.set(uploadId, {
        ...this.activeUploads.get(uploadId),
        status: 'completed',
        result
      });
      
      console.log(`✅ Upload completed successfully using ${uploadMethod} method`);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      // Return consistent result format
      return {
        success: true,
        method: uploadMethod,
        data: result
      };
      
    } catch (error) {
      console.error(`❌ Upload failed:`, error);
      
      // Update tracking
      if (this.activeUploads.has(uploadId)) {
        this.activeUploads.set(uploadId, {
          ...this.activeUploads.get(uploadId),
          status: 'failed',
          error: error.message
        });
      }
      
      if (onError) {
        onError(error);
      }
      
      // Return consistent error format instead of throwing
      return {
        success: false,
        error: error.message || 'Upload failed',
        method: file.size <= MAX_DIRECT_UPLOAD_SIZE ? 'direct' : 'presigned'
      };
    } finally {
      // Clean up tracking after 5 minutes
      setTimeout(() => {
        this.activeUploads.delete(uploadId);
      }, 5 * 60 * 1000);
    }
  }

  /**
   * Direct upload to Lambda (for files ≤6MB)
   */
  async directUpload(uploadId, file, influencerId, folder, onProgress) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('influencer_id', influencerId);
    formData.append('folder', folder);

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Set timeout - even small files can take time on slow connections
      xhr.timeout = BASE_TIMEOUT; // 5 minutes should be enough for 6MB
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress({
            uploadId,
            method: 'direct',
            progress,
            loaded: event.loaded,
            total: event.total
          });
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log(`✅ Direct upload completed successfully`);
            resolve(response);
          } catch (e) {
            console.error(`❌ Invalid response format:`, e);
            reject(new Error('Invalid response format'));
          }
        } else {
          console.error(`❌ Direct upload failed with status ${xhr.status}`);
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        console.error(`❌ Direct upload network error`);
        reject(new Error('Upload failed due to network error'));
      });
      
      xhr.addEventListener('timeout', () => {
        console.error(`⏰ Direct upload timed out after ${BASE_TIMEOUT/1000} seconds`);
        reject(new Error(`Upload timed out after ${BASE_TIMEOUT/1000} seconds`));
      });
      
      xhr.addEventListener('abort', () => {
        console.error(`🚫 Direct upload was aborted`);
        reject(new Error('Upload was aborted'));
      });
      
      // Get the API base URL from the existing api service
      const apiUrl = api.defaults.baseURL || process.env.REACT_APP_API_URL;
      
      console.log(`🚀 Starting direct upload (size: ${this.formatFileSize(file.size)}, timeout: ${BASE_TIMEOUT/1000}s)`);
      xhr.open('POST', `${apiUrl}/api/images/upload`);
      
      // Add authorization header if available
      const token = localStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.send(formData);
    });
  }

  /**
   * Presigned URL upload (for files >6MB)
   */
  async presignedUpload(uploadId, file, influencerId, folder, onProgress) {
    // Step 1: Request presigned URL
    console.log('📝 Requesting presigned URL...');
    
    const requestData = {
      influencer_id: influencerId,
      folder: folder,
      filename: file.name,
      file_size: file.size.toString(),
      content_type: file.type || 'application/octet-stream'
    };

    const presignedResponse = await api.post('/api/images/upload/request', requestData);
    const { upload_id, presigned_post, expires_in, s3_key, metadata } = presignedResponse.data;
    
    console.log(`📤 Got presigned URL, expires in ${expires_in} seconds`);
    
    // Step 2: Upload directly to S3 using presigned POST
    console.log('☁️ Uploading to S3...');
    
    const s3FormData = new FormData();
    
    // Add all the presigned POST fields
    Object.keys(presigned_post.fields).forEach(key => {
      s3FormData.append(key, presigned_post.fields[key]);
    });
    
    // Add the file last (required by S3)
    s3FormData.append('file', file);
    
    // Upload to S3 with progress tracking and retry logic
    await this.uploadToS3WithRetry(
      presigned_post.url,
      s3FormData,
      file.size,
      (progress) => {
        if (onProgress) {
          onProgress({
            uploadId,
            method: 'presigned',
            phase: 'uploading',
            progress: Math.round(progress * 0.8), // Reserve 20% for confirmation
            loaded: Math.round(file.size * progress / 100),
            total: file.size
          });
        }
      }
    );
    
    console.log('✅ S3 upload completed, confirming...');
    
    // Step 3: Confirm upload completion
    if (onProgress) {
      onProgress({
        uploadId,
        method: 'presigned',
        phase: 'confirming',
        progress: 90,
        loaded: file.size,
        total: file.size
      });
    }
    
    const confirmFormData = new FormData();
    confirmFormData.append('upload_id', upload_id);
    
    // Include metadata for cases where upload_tracking table is not available
    confirmFormData.append('influencer_id', influencerId);
    confirmFormData.append('folder', folder);
    confirmFormData.append('filename', file.name);
    confirmFormData.append('file_size', file.size.toString());
    confirmFormData.append('content_type', file.type || 'application/octet-stream');
    confirmFormData.append('s3_key', s3_key);
    
    const confirmResponse = await api.post('/api/images/upload/confirm', confirmFormData);
    
    if (onProgress) {
      onProgress({
        uploadId,
        method: 'presigned',
        phase: 'completed',
        progress: 100,
        loaded: file.size,
        total: file.size
      });
    }
    
    return confirmResponse.data;
  }

  /**
   * Upload to S3 with progress tracking, retry logic, and timeout handling
   */
  async uploadToS3WithProgress(url, formData, totalSize, onProgress, attempt = 1) {
    const isLargeFile = totalSize > LARGE_FILE_THRESHOLD;
    const timeout = isLargeFile ? LARGE_FILE_TIMEOUT : BASE_TIMEOUT;
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Set timeout for the request
      xhr.timeout = timeout;
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log(`✅ S3 upload completed successfully (attempt ${attempt})`);
          resolve();
        } else {
          const error = new Error(`S3 upload failed with status ${xhr.status}: ${xhr.responseText}`);
          console.error(`❌ S3 upload failed (attempt ${attempt}):`, error.message);
          reject(error);
        }
      });
      
      xhr.addEventListener('error', () => {
        const error = new Error('S3 upload failed due to network error');
        console.error(`❌ S3 upload network error (attempt ${attempt}):`, error.message);
        reject(error);
      });
      
      xhr.addEventListener('timeout', () => {
        const error = new Error(`S3 upload timed out after ${timeout/1000} seconds`);
        console.error(`⏰ S3 upload timeout (attempt ${attempt}):`, error.message);
        reject(error);
      });
      
      xhr.addEventListener('abort', () => {
        const error = new Error('S3 upload was aborted');
        console.error(`🚫 S3 upload aborted (attempt ${attempt}):`, error.message);
        reject(error);
      });
      
      console.log(`🚀 Starting S3 upload (attempt ${attempt}, timeout: ${timeout/1000}s, size: ${this.formatFileSize(totalSize)})`);
      xhr.open('POST', url);
      xhr.send(formData);
    });
  }

  /**
   * Upload to S3 with retry logic
   */
  async uploadToS3WithRetry(url, formData, totalSize, onProgress) {
    let lastError;
    
    for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        await this.uploadToS3WithProgress(url, formData, totalSize, onProgress, attempt);
        return; // Success - exit retry loop
      } catch (error) {
        lastError = error;
        
        if (attempt < MAX_RETRY_ATTEMPTS) {
          const delay = RETRY_DELAY_BASE * Math.pow(2, attempt - 1); // Exponential backoff
          console.warn(`⚠️ S3 upload attempt ${attempt} failed, retrying in ${delay/1000}s...`);
          
          // Update progress to show retry status
          if (onProgress) {
            onProgress(0); // Reset progress for retry
          }
          
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          console.error(`❌ S3 upload failed after ${MAX_RETRY_ATTEMPTS} attempts`);
        }
      }
    }
    
    throw lastError;
  }

  /**
   * Validate upload inputs
   */
  validateUploadInputs(file, influencerId, folder) {
    if (!file) {
      throw new Error('File is required');
    }
    
    if (!influencerId) {
      throw new Error('Influencer ID is required');
    }
    
    const validFolders = ['raw', 'filtered', 'manual', 'cropped', 'reference'];
    if (!validFolders.includes(folder)) {
      throw new Error(`Invalid folder. Must be one of: ${validFolders.join(', ')}`);
    }
    
    if (file.size === 0) {
      throw new Error('Cannot upload empty file');
    }
    
    // Check file size limit (100MB max for presigned uploads)
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File size (${this.formatFileSize(file.size)}) exceeds maximum allowed size (${this.formatFileSize(MAX_FILE_SIZE)})`);
    }
  }

  /**
   * Generate unique upload ID
   */
  generateUploadId() {
    return 'upload_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get upload statistics
   */
  getUploadStats() {
    const stats = {
      active: 0,
      completed: 0,
      failed: 0,
      total: this.activeUploads.size
    };
    
    this.activeUploads.forEach(upload => {
      switch (upload.status) {
        case 'started':
          stats.active++;
          break;
        case 'completed':
          stats.completed++;
          break;
        case 'failed':
          stats.failed++;
          break;
      }
    });
    
    return stats;
  }

  /**
   * Cancel an active upload
   */
  cancelUpload(uploadId) {
    if (this.activeUploads.has(uploadId)) {
      this.activeUploads.delete(uploadId);
      return true;
    }
    return false;
  }
}

// Export singleton instance
export const hybridUploadService = new HybridUploadService();

// Export upload utility functions
export const uploadUtils = {
  /**
   * Simple upload function for basic use cases
   */
  async uploadImage(file, influencerId, folder, onProgress = null) {
    return new Promise((resolve, reject) => {
      hybridUploadService.uploadFile(
        file,
        influencerId,
        folder,
        onProgress,
        resolve,
        reject
      );
    });
  },

  /**
   * Get recommended upload method for a file
   */
  getUploadMethod(fileSize) {
    return fileSize <= MAX_DIRECT_UPLOAD_SIZE ? 'direct' : 'presigned';
  },

  /**
   * Check if file size is within limits
   */
  validateFileSize(fileSize) {
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
    return {
      valid: fileSize <= MAX_FILE_SIZE,
      method: fileSize <= MAX_DIRECT_UPLOAD_SIZE ? 'direct' : 'presigned',
      size: fileSize,
      formatted: hybridUploadService.formatFileSize(fileSize)
    };
  }
};

export default hybridUploadService;